import React, { useState, useEffect } from 'react';
import { FileText, Loader2, ArrowUpDown, IndianRupee, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerLedger = () => {
  const [partner, setPartner] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [feePerWill, setFeePerWill] = useState(500);
  const [feePerRenewal, setFeePerRenewal] = useState(250);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const partnerToken = localStorage.getItem('partnerToken');
  const axiosConfig = { headers: { Authorization: `Bearer ${partnerToken}` } };

  useEffect(() => {
    fetchLedger();
  }, []);

  const fetchLedger = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/partner/ledger`, axiosConfig);
      setPartner(response.data.partner);
      setLedgerEntries(response.data.entries || []);
      setFeePerWill(response.data.fee_per_will || 500);
      setFeePerRenewal(response.data.fee_per_renewal || 250);
    } catch (error) {
      toast.error('Failed to fetch ledger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEntries = React.useMemo(() => {
    if (!sortConfig.key) return ledgerEntries;
    
    return [...ledgerEntries].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (sortConfig.key === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      return sortConfig.direction === 'asc' 
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [ledgerEntries, sortConfig]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const istOptions = { 
      timeZone: 'Asia/Kolkata',
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-IN', istOptions) + ' IST';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    const isNegative = amount < 0;
    const formatted = `₹${Math.abs(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return isNegative ? `-${formatted}` : formatted;
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'will_purchase': return 'Will Purchase';
      case 'will_renewal': return 'Will Renewal';
      case 'payment_from_willkart': return 'Payment from WillKart';
      default: return type;
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'will_purchase':
      case 'will_renewal':
        return 'text-emerald-400 bg-emerald-400/10';
      case 'payment_from_willkart':
        return 'text-blue-400 bg-blue-400/10';
      default:
        return 'text-slate-400 bg-slate-400/10';
    }
  };

  // Calculate summary stats
  const stats = React.useMemo(() => {
    const lastEntry = ledgerEntries.length > 0 
      ? [...ledgerEntries].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      : null;
    
    const totalWillPurchases = ledgerEntries
      .filter(e => e.transaction_type === 'will_purchase' || e.transaction_type === 'will_renewal')
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    
    const totalPayments = ledgerEntries
      .filter(e => e.transaction_type === 'payment_from_willkart')
      .reduce((sum, e) => sum + Math.abs(e.amount || 0), 0);
    
    return {
      currentBalance: lastEntry?.cumulative_total || 0,
      totalWillPurchases,
      totalPayments,
      totalEntries: ledgerEntries.length
    };
  }, [ledgerEntries]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-ledger-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Ledger</h1>
        <p className="text-slate-400 mt-1">Your account statement with WillKart</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-slate-400 text-sm">Current Balance</p>
                <p className={`text-2xl font-bold mt-1 ${stats.currentBalance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(stats.currentBalance)}
                </p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-xl">
                <IndianRupee className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Total Will Revenue</p>
            <p className="text-xl font-bold text-emerald-400 mt-1">
              {formatCurrency(stats.totalWillPurchases)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Payments Received</p>
            <p className="text-xl font-bold text-blue-400 mt-1">
              {formatCurrency(stats.totalPayments)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <p className="text-slate-400 text-sm">WillKart Fees</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="w-3 h-3 text-slate-500" />
                  </TooltipTrigger>
                  <TooltipContent className="bg-slate-800 border-slate-700">
                    <p className="text-xs">Amounts deducted per transaction type</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex gap-4 mt-1">
              <div>
                <p className="text-xs text-slate-500">Creation</p>
                <p className="text-lg font-bold text-amber-400">{formatCurrency(feePerWill)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Renewal</p>
                <p className="text-lg font-bold text-amber-400">{formatCurrency(feePerRenewal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-400" />
              Transaction History
            </CardTitle>
            <span className="text-slate-400 text-sm">{stats.totalEntries} entries</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {ledgerEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-400">No transactions yet</p>
              <p className="text-slate-500 text-sm mt-1">Transactions will appear here when your clients purchase wills</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ledger-table">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-800/30">
                    <th 
                      className="text-left p-3 text-slate-400 font-medium text-sm cursor-pointer hover:text-white"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Date/Time (IST)
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="text-left p-3 text-slate-400 font-medium text-sm">Client</th>
                    <th className="text-left p-3 text-slate-400 font-medium text-sm">Type</th>
                    <th 
                      className="text-right p-3 text-slate-400 font-medium text-sm cursor-pointer hover:text-white"
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Amount
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="text-right p-3 text-slate-400 font-medium text-sm">To WillKart</th>
                    <th className="text-right p-3 text-slate-400 font-medium text-sm">Net Amount</th>
                    <th 
                      className="text-right p-3 text-slate-400 font-medium text-sm cursor-pointer hover:text-white"
                      onClick={() => handleSort('cumulative_total')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Cumulative
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="text-left p-3 text-slate-400 font-medium text-sm">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry, idx) => (
                    <tr 
                      key={entry.id || idx}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="p-3 text-slate-300 text-sm whitespace-nowrap">
                        {formatDateTime(entry.created_at)}
                      </td>
                      <td className="p-3 text-white text-sm">
                        {entry.client_name || '—'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionTypeColor(entry.transaction_type)}`}>
                          {getTransactionTypeLabel(entry.transaction_type)}
                        </span>
                      </td>
                      <td className={`p-3 text-right text-sm font-medium ${entry.amount >= 0 ? 'text-emerald-400' : 'text-blue-400'}`}>
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="p-3 text-right text-amber-400 text-sm">
                        {entry.amount_to_willkart > 0 ? formatCurrency(entry.amount_to_willkart) : '—'}
                      </td>
                      <td className={`p-3 text-right text-sm font-medium ${entry.net_amount >= 0 ? 'text-emerald-400' : 'text-blue-400'}`}>
                        {formatCurrency(entry.net_amount)}
                      </td>
                      <td className={`p-3 text-right text-sm font-bold ${entry.cumulative_total >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {formatCurrency(entry.cumulative_total)}
                      </td>
                      <td className="p-3 text-slate-400 text-sm">
                        {entry.reference_number || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerLedger;
